import { zodResolver } from "@hookform/resolvers/zod";
import { blockersFromFormErrors, refuseNow } from "@/lib/missing-fields";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { Eye, EyeOff, Mail } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { SeedlingMark } from "@/components/app/seedling-mark";
import { ThemeToggle } from "@/components/app/theme-toggle";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Field } from "@/components/ui/field";
import { Input, InputGroup } from "@/components/ui/input";
import {
  LoginJourney,
  PanelSprout,
} from "@/features/auth/components/login-journey";
import { deviceLabel } from "@/features/audit/device";
import { useAuth } from "@/features/auth/store";
import { ApiError } from "@/lib/api-client";

const schema = z.object({
  email: z.email("Enter your work e-mail"),
  password: z.string().min(1, "Enter your password"),
});
type Form = z.infer<typeof schema>;

/** The other system named by a 409 `already_logged_in` (apps/api/src/modules/auth/service.ts). */
interface OtherSystem {
  client: "web" | "native";
  ip: string | null;
  userAgent: string | null;
  lastActivityAt: string;
}

const SIGNED_OUT: Record<"idle" | "replaced", string> = {
  idle: "You were signed out after 30 minutes of inactivity. Sign in again to carry on.",
  replaced:
    "You were signed out because your account logged in on another system.",
};

function minutesAgo(iso: string): string {
  const m = Math.max(
    0,
    Math.round((Date.now() - new Date(iso).getTime()) / 60_000)
  );
  return m < 1 ? "just now" : m === 1 ? "1 min ago" : `${m} min ago`;
}

export const Route = createFileRoute("/login")({
  validateSearch: z.object({
    redirect: z.string().optional(),
    signedOut: z.enum(["idle", "replaced"]).optional().catch(undefined),
  }),
  beforeLoad: async () => {
    if (await useAuth.getState().bootstrap()) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const login = useAuth((s) => s.login);
  const [showPassword, setShowPassword] = useState(false);
  // Set when the account is already signed in on another browser: the panel offers to sign it out.
  const [conflict, setConflict] = useState<{
    other: OtherSystem;
    values: Form;
  } | null>(null);
  const [forcing, setForcing] = useState(false);
  const { register, handleSubmit, formState } = useForm<Form>({
    resolver: zodResolver(schema),
  });
  const { errors, isSubmitting } = formState;

  const submit = handleSubmit(
    async (values) => {
      setConflict(null);
      try {
        await login(values.email, values.password);
        await navigate({ to: search.redirect ?? "/" });
      } catch (e) {
        if (e instanceof ApiError && e.code === "already_logged_in") {
          setConflict({ other: e.details as OtherSystem, values });
          return;
        }
        // The window message names a date and is worth repeating verbatim: the password already
        // verified, so it leaks nothing, and "invalid email or password" would send someone to
        // reset a password that is perfectly fine. The same goes for a handheld-only account.
        toast.error(
          e instanceof ApiError &&
            (e.code === "access_window_inactive" ||
              e.code === "no_access_on_device")
            ? e.message
            : e instanceof ApiError && e.status === 401
              ? "Invalid email or password"
              : "Could not sign in. Try again."
        );
      }
    },
    // Names what is missing in the centred notice and aims the jump (lib/missing-fields).
    (errors) => refuseNow(blockersFromFormErrors(errors))
  );

  return (
    <main className="relative grid min-h-full grid-rows-[auto_1fr] bg-surface lg:grid-cols-[1.1fr_1fr] lg:grid-rows-none">
      {/* Brand panel: a band on phones, the left half from 1024px. */}
      <div className="relative isolate flex flex-col overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800 px-4 pt-5 pb-7 text-primary-ink sm:px-8 lg:justify-between lg:p-12 xl:p-16 dark:bg-none dark:bg-primary-soft dark:text-primary-text">
        <PanelSprout className="absolute -right-24 -bottom-20 -z-10 hidden h-[34rem] w-[34rem] opacity-10 lg:block" />

        <h1 className="flex items-center gap-3 text-2xl font-semibold tracking-tight lg:text-3xl">
          {/* 24% matches the tile's rx 24 on its 100 grid at any size. */}
          <span className="rounded-[24%] ring-1 ring-primary-ink/30 dark:ring-primary-text/30">
            <SeedlingMark className="h-8 w-8 lg:h-10 lg:w-10" />
          </span>
          NexSeed
        </h1>

        <LoginJourney className="my-10 hidden max-w-xl lg:block" />

        <div className="mt-6 lg:mt-0">
          <p className="text-2xl font-semibold tracking-[-0.02em] lg:text-5xl lg:leading-[3.75rem]">
            Every lot, gate to dispatch.
          </p>
          <p className="mt-2 max-w-[44ch] lg:mt-4 lg:text-lg">
            Arrival, quality, processing, packaging and dispatch for VNR Seeds,
            on one record.
          </p>
          <p className="mt-10 hidden text-sm lg:block">
            VNR Seeds Pvt Ltd · Internal system
          </p>
        </div>
      </div>

      <ThemeToggle className="absolute top-4 right-4 z-10 lg:top-6 lg:right-6" />

      {/* Form column */}
      <div className="flex flex-col px-4 py-8 sm:px-8 lg:justify-center lg:py-16">
        <div className="mx-auto w-full max-w-sm motion-safe:animate-rise">
          <h2 className="text-2xl font-semibold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-ink-muted">
            Warehouse operations for VNR Seeds
          </p>

          {search.signedOut && !conflict && (
            <Callout tone="warning" className="mt-6">
              {SIGNED_OUT[search.signedOut]}
            </Callout>
          )}

          {conflict && (
            <Callout
              tone="warning"
              className="mt-6"
              title="Already logged in from another system"
            >
              <p className="mt-1">
                {deviceLabel(conflict.other)}
                {conflict.other.ip ? ` · ${conflict.other.ip}` : ""} · active{" "}
                {minutesAgo(conflict.other.lastActivityAt)}
              </p>
              <p className="mt-1">
                Log out from that system first, or sign it out from here. Its
                unsaved work will be lost.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  loading={forcing}
                  onClick={() => {
                    const { email, password } = conflict.values;
                    setForcing(true);
                    login(email, password, true)
                      .then(() => navigate({ to: search.redirect ?? "/" }))
                      .catch((e: unknown) => {
                        setConflict(null);
                        toast.error(
                          e instanceof ApiError
                            ? e.message
                            : "Could not sign in. Try again."
                        );
                      })
                      .finally(() => setForcing(false));
                  }}
                >
                  Sign out other system and continue
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setConflict(null)}
                >
                  Cancel
                </Button>
              </div>
            </Callout>
          )}

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => void submit(e)}
            noValidate
          >
            <Field label="E-mail" htmlFor="email" error={errors.email?.message}>
              <InputGroup leading={<Mail aria-hidden />}>
                <Input
                  id="email"
                  type="email"
                  size="lg"
                  autoComplete="username"
                  autoFocus
                  invalid={!!errors.email}
                  aria-describedby={errors.email ? "email-err" : undefined}
                  {...register("email")}
                />
              </InputGroup>
            </Field>

            <Field
              label="Password"
              htmlFor="password"
              error={errors.password?.message}
            >
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  size="lg"
                  autoComplete="current-password"
                  className="pr-12"
                  invalid={!!errors.password}
                  aria-describedby={
                    errors.password ? "password-err" : undefined
                  }
                  {...register("password")}
                />
                {/* Named by its content, not aria-label: getByLabel("Password") must find only the field. */}
                <button
                  type="button"
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-md text-ink-muted transition-colors hover:text-ink focus-visible:ring-2 focus-visible:ring-focus/50 focus-visible:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                  <span className="sr-only">
                    {showPassword ? "Hide password" : "Show password"}
                  </span>
                </button>
              </div>
            </Field>

            <Link
              to="/forgot-password"
              className="-mt-2 self-end text-sm text-primary-text hover:underline"
            >
              Forgot password?
            </Link>

            <Button
              type="submit"
              size="lg"
              className="mt-1 w-full"
              loading={isSubmitting}
            >
              {isSubmitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 border-t border-border pt-5 text-sm text-ink-muted">
            Access is by invitation from your administrator.
          </p>
        </div>
      </div>
    </main>
  );
}
